/** A hand-authored map, as a file.
 *
 * A save is a `GameState` - a game in progress, with fog, an RNG position, a
 * turn and a battle. A *scenario* is the thing a game is built out of: terrain,
 * where the players start, what is on the board, and what winning means. Until
 * now the only way to get one was `generateMap`, so there was no way to play a
 * map anybody had drawn and nothing for a map editor to write.
 *
 * The format is deliberately the kernel's own `NewGameSetup` with an envelope
 * around it. That is not laziness: it means the load path is
 * `parseScenario -> newGame`, which is exactly the path `startGame` already
 * takes for a generated map, and there is no second notion of "what a map is"
 * that could drift from the one the kernel accepts. A scenario that parses is a
 * scenario `newGame` will build, and anything `newGame` refuses to honour - a
 * win condition that cannot be met on this map, say - it rewrites in the open
 * rather than failing at load.
 *
 * Untrusted input, so everything below is validated and *returned* as an `Err`
 * (Section 3.2). A hand-edited map file is the most likely thing anybody will
 * ever feed this kernel, and "the tab went white" is not a diagnosis.
 */

import type { ContentRegistry } from "../content/registry.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";
import type { AdventureMapState } from "../state.js";
import { TERRAINS } from "../state.js";
import type { NewGameSetup } from "../adventure/create.js";
import { decodeTree, encodeTree, parseJson, serialize, type Json } from "./codec.js";
import type { PersistError } from "./types.js";
import { malformedFile } from "./types.js";

export const SCENARIO_KIND = "realm.scenario";
/**
 * Its own counter, not the save's.
 *
 * A scenario has no `GameState` in it, so the save's migration chain does not
 * apply and sharing the number would mean bumping a map file's version every
 * time a rule changed under a save.
 */
export const SCENARIO_FORMAT_VERSION = 1;

/** What a player is told about a map before choosing it. */
export interface ScenarioMeta {
  readonly name: string;
  readonly description: string;
  readonly author: string;
  /** Free text, e.g. "1v1", "underground". Sorted, so two writes match. */
  readonly tags: readonly string[];
}

export const EMPTY_META: ScenarioMeta =
  Object.freeze({ name: "Untitled", description: "", author: "", tags: [] });

export interface ScenarioFile {
  readonly kind: typeof SCENARIO_KIND;
  readonly formatVersion: number;
  /** The content bundle the map was authored against; a warning, not a gate. */
  readonly contentVersion: string;
  readonly meta: ScenarioMeta;
  readonly setup: NewGameSetup;
}

export const SCENARIO_KEYS = ["contentVersion", "formatVersion", "kind", "meta", "setup"] as const;
export const META_KEYS = ["author", "description", "name", "tags"] as const;

// ------------------------------------------------------------------- write

export function makeScenario(
  setup: NewGameSetup, meta: Partial<ScenarioMeta> = {}, contentVersion = "",
): ScenarioFile {
  return {
    kind: SCENARIO_KIND,
    formatVersion: SCENARIO_FORMAT_VERSION,
    contentVersion: setup.contentVersion ?? contentVersion,
    meta: {
      name: meta.name ?? EMPTY_META.name,
      description: meta.description ?? EMPTY_META.description,
      author: meta.author ?? EMPTY_META.author,
      tags: [...(meta.tags ?? [])].sort(),
    },
    setup,
  };
}

/** Canonical JSON, like a save: two writes of the same map are the same bytes. */
export const serializeScenario = (file: ScenarioFile): string => serialize(encodeTree(file));

export const saveScenario = (
  setup: NewGameSetup, meta?: Partial<ScenarioMeta>, contentVersion?: string,
): string => serializeScenario(makeScenario(setup, meta, contentVersion));

/** JSON-safe data rather than text, for a host that would rather not stringify. */
export const scenarioToJson = (file: ScenarioFile): Json => encodeTree(file);

// -------------------------------------------------------------- validation

const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

function requireKeys(
  o: Record<string, unknown>, keys: readonly string[], what: string,
): PersistError | null {
  const present = Object.keys(o).sort();
  const missing = keys.filter((k) => !present.includes(k));
  if (missing.length > 0) return malformedFile(`${what} is missing ${missing.join(", ")}`);
  const extra = present.filter((k) => !keys.includes(k));
  // Extra keys are refused rather than ignored for the same reason the save
  // envelope refuses them: an unread field is where presentation state moves in.
  if (extra.length > 0) return malformedFile(`${what} has unexpected keys: ${extra.join(", ")}`);
  return null;
}

const isPlainInt = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v);

/**
 * The map plane, checked against itself.
 *
 * Every dense array has to be exactly `width * height * levels` long, because
 * the kernel indexes them without bounds checks: a short `terrain` is not a
 * corrupt map, it is an out-of-range read on every cell past the end of it.
 */
function validateMap(v: unknown): Result<AdventureMapState, PersistError> {
  if (!isObject(v)) return Err(malformedFile("setup.map is not an object"));
  const w = v["width"], h = v["height"], l = v["levels"];
  if (!isPlainInt(w) || !isPlainInt(h) || !isPlainInt(l)) {
    return Err(malformedFile("setup.map: width, height and levels must be integers"));
  }
  if (w <= 0 || h <= 0 || l <= 0) {
    return Err(malformedFile("setup.map: width, height and levels must be positive"));
  }
  // 252 is the largest size the New Game screen offers; four planes is two more
  // than the game has. Both are here to stop a hand-edited header asking for a
  // billion-cell allocation before anything else is read.
  if (w > 252 || h > 252 || l > 4) {
    return Err(malformedFile(`setup.map: ${String(w)}x${String(h)}x${String(l)} is larger than any map this game builds`));
  }
  const n = w * h * l;
  for (const key of ["terrain", "variant", "road", "river", "moveCost"] as const) {
    const arr = v[key];
    if (!ArrayBuffer.isView(arr) || !("length" in arr)) {
      return Err(malformedFile(`setup.map.${key} is not a typed array`));
    }
    const len = (arr as unknown as { length: number }).length;
    if (len !== n) {
      return Err(malformedFile(
        `setup.map.${key} has ${String(len)} cells; the header says ${String(n)}`,
      ));
    }
  }
  const terrain = v["terrain"] as unknown as ArrayLike<number>;
  for (let i = 0; i < n; i++) {
    const t = terrain[i] ?? 0;
    if (t >= TERRAINS.length) {
      return Err(malformedFile(`setup.map.terrain[${String(i)}]: ${String(t)} is not a terrain`));
    }
  }
  return Ok(v as unknown as AdventureMapState);
}

/**
 * The setup, checked for the things `newGame` would crash on rather than refuse.
 *
 * `newGame` is written against setups this codebase produced and is entitled to
 * assume its input is well formed; a file off a stranger's disk is not. So the
 * gate is here, and the rule is narrow: refuse what would index out of bounds
 * or divide by zero, and let `newGame` deal with everything that is merely
 * unwise. A map with one player is a sandbox, not a malformed file.
 */
export function validateScenarioSetup(v: unknown): Result<NewGameSetup, PersistError> {
  if (!isObject(v)) return Err(malformedFile("setup is not an object"));
  if (!isPlainInt(v["rootSeed"])) return Err(malformedFile("setup.rootSeed is not an integer"));

  const map = validateMap(v["map"]);
  if (!map.ok) return map;
  const m = map.value;

  const players = v["players"];
  if (!Array.isArray(players) || players.length === 0) {
    return Err(malformedFile("setup.players is empty; a scenario needs at least one"));
  }
  const seen = new Set<string>();
  for (const [i, p] of players.entries()) {
    if (!isObject(p)) return Err(malformedFile(`setup.players[${String(i)}] is not an object`));
    const id = p["id"];
    if (typeof id !== "string" || id === "") {
      return Err(malformedFile(`setup.players[${String(i)}].id is not a player id`));
    }
    if (seen.has(id)) return Err(malformedFile(`setup.players: ${id} appears twice`));
    seen.add(id);
    const kind = p["kind"];
    if (kind !== "human" && kind !== "ai" && kind !== "none") {
      return Err(malformedFile(`setup.players[${String(i)}].kind is not human, ai or none`));
    }
    const bad = outOfBounds(p["towns"], m, `setup.players[${String(i)}].towns`)
      ?? outOfBounds(p["heroes"], m, `setup.players[${String(i)}].heroes`);
    if (bad !== null) return Err(bad);
  }

  const bad = outOfBounds(v["objects"], m, "setup.objects")
    ?? outOfBounds(v["neutralTowns"], m, "setup.neutralTowns");
  if (bad !== null) return Err(bad);

  return Ok(v as unknown as NewGameSetup);
}

/** Anything with x/y/z has to land on the map that was declared. */
function outOfBounds(
  list: unknown, m: AdventureMapState, path: string,
): PersistError | null {
  if (list === undefined || list === null) return null;
  if (!Array.isArray(list)) return malformedFile(`${path} is not a list`);
  for (const [i, e] of list.entries()) {
    if (!isObject(e)) return malformedFile(`${path}[${String(i)}] is not an object`);
    const x = e["x"], y = e["y"], z = e["z"];
    if (!isPlainInt(x) || !isPlainInt(y) || !isPlainInt(z)) {
      return malformedFile(`${path}[${String(i)}] has no integer x, y and z`);
    }
    if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) {
      return malformedFile(
        `${path}[${String(i)}] is at (${String(x)}, ${String(y)}, ${String(z)}), off a `
        + `${String(m.width)}x${String(m.height)}x${String(m.levels)} map`,
      );
    }
  }
  return null;
}

// -------------------------------------------------------------------- read

/**
 * Text to a scenario. The order is parse, decode, then validate - the reverse
 * of the save path, and for a reason: a save is migrated before it is
 * validated, and a scenario has nothing to migrate, so the typed arrays have to
 * be real before the length checks can mean anything.
 */
export function parseScenario(text: string): Result<ScenarioFile, PersistError> {
  const raw = parseJson(text);
  if (!raw.ok) return raw;
  const decoded = decodeTree(raw.value);
  if (!decoded.ok) return decoded;
  return scenarioFromTree(decoded.value);
}

export function scenarioFromTree(v: unknown): Result<ScenarioFile, PersistError> {
  if (!isObject(v)) return Err(malformedFile("the root of a scenario is an object"));
  if (v["kind"] !== SCENARIO_KIND) {
    return Err(malformedFile(
      `this is not a map file: kind is ${JSON.stringify(v["kind"])}, expected "${SCENARIO_KIND}"`,
    ));
  }
  const envelope = requireKeys(v, [...SCENARIO_KEYS], "a scenario");
  if (envelope !== null) return Err(envelope);

  const version = v["formatVersion"];
  if (!isPlainInt(version)) return Err(malformedFile("formatVersion is not an integer"));
  if (version > SCENARIO_FORMAT_VERSION) {
    return Err(malformedFile(
      `this map was written by a newer build (format ${String(version)}; this one reads `
      + `${String(SCENARIO_FORMAT_VERSION)})`,
    ));
  }
  if (typeof v["contentVersion"] !== "string") {
    return Err(malformedFile("contentVersion is not a string"));
  }

  const meta = v["meta"];
  if (!isObject(meta)) return Err(malformedFile("meta is not an object"));
  const metaKeys = requireKeys(meta, [...META_KEYS], "meta");
  if (metaKeys !== null) return Err(metaKeys);
  if (typeof meta["name"] !== "string" || typeof meta["description"] !== "string"
    || typeof meta["author"] !== "string") {
    return Err(malformedFile("meta.name, meta.description and meta.author are strings"));
  }
  const tags = meta["tags"];
  if (!Array.isArray(tags) || tags.some((t) => typeof t !== "string")) {
    return Err(malformedFile("meta.tags is a list of strings"));
  }

  const setup = validateScenarioSetup(v["setup"]);
  if (!setup.ok) return setup;

  return Ok({
    kind: SCENARIO_KIND,
    formatVersion: version,
    contentVersion: v["contentVersion"],
    meta: {
      name: meta["name"], description: meta["description"], author: meta["author"],
      tags: tags as readonly string[],
    },
    setup: setup.value,
  });
}

/**
 * Whether a map was authored against the content this build ships.
 *
 * Not a gate. A map whose objects were renamed under it will lose those
 * objects, and a map from a build with one more creature in it will play fine;
 * neither is worth refusing to open, and both are worth saying out loud.
 */
export function scenarioContentWarning(
  file: ScenarioFile, content: ContentRegistry,
): string | null {
  const want = file.contentVersion;
  if (want === "" || want === content.contentVersion) return null;
  return `This map was drawn against content "${want}"; this build ships `
    + `"${content.contentVersion}". Objects it names that no longer exist will be missing.`;
}
